import { FormGroup } from '@blueprintjs/core'
import React from 'react'
import CX from 'classnames'
import Client from '../../../../../client'
import { LoadableSelect } from '../../../../../components/Inputs'
import config from '../../../../../config'
import { UprightStyle } from '../../../../../types'
import { filterByConstraints, gatingAspect, SiblingValues } from './constraints'

interface Props {
  categoryMarketCategoryId: any
  specificName: any
  createable: boolean
  value: string
  required: boolean
  onChange: (v: string) => void
  allowDangerIntent?: boolean
  relevanceSearchCount?: number
  // Aspects that must be answered before this one can be offered, e.g. Size Type depends on Size.
  constrainedBy?: string[]
  // Every specific's current value, so cross-aspect compatibility can be resolved locally.
  siblingValues?: SiblingValues
}

const styles: UprightStyle = {
  zeroMarginBottom: { marginBottom: 0 }
}

export default class SpecificRenderer extends React.PureComponent<Props> {
  fetchRecommendations = () => {
    const { categoryMarketCategoryId, specificName } = this.props
    return Client.categorySpecificRecommendations(categoryMarketCategoryId, specificName)
  }

  // The aspect this one waits on, or null when it is ready to be answered.
  // LoadableSelect caches its options in local state after the first load, so it must be
  // remounted when a value it was filtered against changes. Keyed on only the constraining
  // aspects, so editing an unrelated specific does not refetch.
  get constraintKey(): string {
    const { constrainedBy, siblingValues } = this.props
    return (constrainedBy || []).map(name => `${name}=${siblingValues?.[name] ?? ''}`).join('|')
  }

  get gatedBy(): string | null {
    return gatingAspect(this.props.constrainedBy, this.props.siblingValues || {})
  }

  // Set while processing loaded options: true when the saved value is one the chosen sibling
  // values no longer permit. Such a value stays selectable and is flagged, so the user can see
  // and correct it rather than having it vanish silently.
  state = { valueIsIncompatible: false }

  processRecommendations = d => {
    const { createable, value } = this.props
    const compatible = filterByConstraints(d.valueRecommendations, this.props.siblingValues || {})

    const incompatible = !!value && !!this.props.constrainedBy?.length && !compatible.some(vr => vr.value === value)
    // Guarded so processing options never triggers an avoidable re-render.
    if (this.state.valueIsIncompatible !== incompatible) this.setState({ valueIsIncompatible: incompatible })

    const valueRecommendations = compatible.map(vr => ({
      value: vr.value,
      label: vr.value
    }))

    // insert a created value, only if the input:
    // - is createable,
    // - has an actual value (not undefined),
    // - does not already exist in the list
    if (createable && value && !valueRecommendations.find(vr => vr.value === value)) {
      valueRecommendations.push({
        value,
        label: value
      })
    }

    // A saved value the constraints now exclude is re-added so the user sees what must change.
    // Without this it would disappear from the form with no explanation.
    if (value && this.props.constrainedBy?.length && !valueRecommendations.find(vr => vr.value === value)) {
      const knownValue = d.valueRecommendations.find(vr => vr.value === value)
      if (knownValue) valueRecommendations.push({ value, label: value })
    }

    if (config.EBAY_AUTOFILL_SPECIFICS()) {
      if (!this.props.createable && value && !valueRecommendations.find(vr => vr.value === value)) {
        const correctlyCasedValue = valueRecommendations.find(vr => vr.value.toUpperCase() === value.toUpperCase())
        if (correctlyCasedValue) {
          // Their template had a valid value, but with the wrong case, like "beagle" instead of "Beagle". Fix it for them.
          this.onChange(correctlyCasedValue)
        } else {
          // This was provided with an invalid value (probably from a builder template), so remove it.
          // Because we have to make an API call per specific to get possible values to determine if autofilled specifics are valid,
          // this component is a convenient place to do this check.
          // We could get by without this. Amazingly, ebay's API will allow us to send up invalid values (and they display on the item page!)
          // even though free-text isn't allowed. But let's not depend on this.
          this.onChange(null)
        }
      }
    }

    return valueRecommendations
  }

  onChange = (v: { value: string }) => {
    this.props.onChange(v && v.value)
  }

  render() {
    const { required, specificName, value, createable, allowDangerIntent } = this.props
    const { gatedBy } = this

    if (gatedBy) {
      return (
        <FormGroup
          label={specificName}
          labelInfo={required && 'Required'}
          style={styles.zeroMarginBottom}
          className={CX('float-right-formgroup-label', 'nuke-top-margin-loadable-select')}
        >
          <div className={CX('bp5-input', 'bp5-disabled', 'ebay-specific-gated')}>{`Select a ${gatedBy} first`}</div>
          <div className="relevance-search-count bp5-text-muted"> </div>
        </FormGroup>
      )
    }

    return (
      <FormGroup
        label={specificName}
        labelInfo={required && 'Required'}
        style={styles.zeroMarginBottom}
        className={CX('float-right-formgroup-label', 'nuke-top-margin-loadable-select')}
      >
        <LoadableSelect
          key={this.constraintKey}
          load={this.fetchRecommendations}
          process={this.processRecommendations}
          simpleValue={false}
          onChange={this.onChange}
          virtualized
          autoload={!!value}
          value={value}
          createable={createable}
          placeholder={createable ? 'Create or Select' : 'Select'}
          ignoreAccents={false}
          // react-select-same-height-as-blueprint - because often times these specifics will be on the same line
          // as a blueprint InputGroup, like for "Condition Description" or ConditionDescriptorPicker
          className="react-select-same-height-as-blueprint"
          additionalClassName="ebay-specific-select"
          intent={((allowDangerIntent && required && !value) || this.state.valueIsIncompatible) && 'danger'}
        />
        <div className="relevance-search-count bp5-text-muted">
          {
            this.props.relevanceSearchCount && this.props.relevanceSearchCount > 100
              ? `${this.props.relevanceSearchCount.toLocaleString()} searches`
              : ' ' // every specific needs _some_ string here since this serves as the margin between rows
          }
        </div>
      </FormGroup>
    )
  }
}
